import { Button, Pagination, Spin } from "antd";
import { Box, CheckSquare, Package, Search, Square } from "lucide-react";
import { Dispatch, SetStateAction, use, useEffect, useMemo, useState } from "react";
import { useDebounceValue } from "~/hooks/use-debounce";
import { useGetInventories } from "~/services/inventory/inventory.queries";
import { CheckType, InventoryType, METADATA_DEFAULT, PackingTypes, ContainerType } from "~/utils/constants";
import { TInventory } from "~/types/inventory";
import { PrintLabels } from "./print-label";
import { MetaData } from "~/types/common";

interface PrintByInventoryProps {
  selectedItems: string[];
  toggleSelection: (id: string) => void;
  selectedClient: string;
  setSelectedClient: Dispatch<SetStateAction<string>>;
  selectedLocation: string;
  setSelectedLocation: Dispatch<SetStateAction<string>>;
  compactView: boolean;
  itemType: InventoryType | "all";
  setCompactView: Dispatch<SetStateAction<boolean>>;
  setSelectedItems: Dispatch<SetStateAction<string[]>>;
  onSelectedInventories: (inventories: TInventory[]) => void;
}

export const PrintByInventory: React.FC<PrintByInventoryProps> = ({
  selectedItems,
  toggleSelection,
  selectedLocation,
  compactView,
  setSelectedItems,
  selectedClient,
  itemType,
  onSelectedInventories,
}) => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [search] = useDebounceValue(searchTerm, 500);
  const [metaData, setMetaData] = useState<MetaData>(METADATA_DEFAULT);

  const filterParams = useMemo(() => {
    const params: Record<string, any> = {
      page,
      pageSize: 10,
    };
    if (search) {
      params.search = search;
    }

    if (itemType !== "all") {
      params.itemType = itemType;
    }

    if (selectedLocation !== "all") {
      params.locationId = selectedLocation;
    }

    if (selectedClient !== "all") {
      params.clientId = selectedClient;
    }

    return params;
  }, [itemType, page, search, selectedClient, selectedLocation]);

  const { data: inventories, isLoading } = useGetInventories({
    checkParentId: CheckType.CHECK,
    ...filterParams,
  });

  // Memoize selectedInventories based on selectedItems
  const selectedInventories = useMemo(() => {
    return inventories?.items.filter((item: TInventory) =>
      selectedItems.includes(item.id)
    ) || [];
  }, [inventories, selectedItems]);

  useEffect(() => {
    if (onSelectedInventories) {
      onSelectedInventories(selectedInventories);
    }
  }, [selectedInventories, onSelectedInventories]);

  useEffect(() => {
    if (inventories?.meta && inventories.meta.totalItems !== metaData.totalItems) {
      setMetaData(inventories.meta);
    }
  }, [inventories?.meta]);

  // Format packing type with container type fallback
  const formatPackingType = (packingType?: PackingTypes, containerType?: ContainerType) => {
    // If it's a container type, use that instead
    if (containerType) {
      const containerMap: Record<ContainerType, string> = {
        [ContainerType.CRATE]: "Crate",
        [ContainerType.PACKAGE]: "Package",
        [ContainerType.PIECE]: "Piece",
        [ContainerType.T_Frame]: "T Frame",
        [ContainerType.SLIPCASE]: "Slipcase",
        [ContainerType.BOX]: "Box",
        [ContainerType.CUSTOM]: "Custom",
        [ContainerType.SLATCRATE]: "Slat Crate",
        [ContainerType.CBIN]: "C-Bin",
        [ContainerType.PALLET]: "Pallet",
      };
      return containerMap[containerType] || containerType;
    }

    if (!packingType) return "N/A";

    const packingMap: Record<PackingTypes, string> = {
      [PackingTypes.LOOSE]: "Loose",
      [PackingTypes.SLIPCASE]: "Slipcase",
      [PackingTypes.CB_FOLD]: "Cardboard Fold",
      [PackingTypes.SHADOW_BOX]: "Shadow Box",
      [PackingTypes.FLOATING_SHADOW_BOX]: "Floating Shadow Box",
      [PackingTypes.POLY_ONLY]: "Poly Only",
      [PackingTypes.TRAVEL_FRAME]: "Travel Frame",
      [PackingTypes.CRATE]: "Crate",
      [PackingTypes.SLATCRATE]: "Slat Crate",
      [PackingTypes.PALLET]: "Pallet",
      [PackingTypes.COMMERCIAL_BIN]: "Commercial Bin",
      [PackingTypes.CBIN]: "C-Bin",
      [PackingTypes.UNKNOWN_PACKAGING]: "Unknown Packaging",
      [PackingTypes.BOX]: "Box",
      [PackingTypes.CUSTOM]: "Custom",
      [PackingTypes.T_Frame]: "T Frame",
      [PackingTypes.BLANKET_WRAPPED]: "Blanket Wrapped",
      [PackingTypes.POLY_FOAM]: "Poly/Foam",
      [PackingTypes.POLY_FOAM_CB]: "Poly/Foam/CB",
    };

    return packingMap[packingType] || packingType;
  };

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium">Inventory Items</h2>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500">
            {selectedItems.length} selected
          </div>
          <PrintLabels selectedInventories={selectedInventories} />
        </div>
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <div className="p-3 border-b flex justify-between items-center bg-gray-50">
          <div className="flex items-center space-x-3">
            <button
              className="p-1"
              onClick={() => {
                const allIds = inventories?.items.map((item) => item.id);
                if (selectedItems.length === allIds?.length) {
                  setSelectedItems([]);
                } else {
                  setSelectedItems(allIds || []);
                }
              }}
            >
              {selectedItems.length === inventories?.items.length &&
                inventories?.items.length > 0 ? (
                <CheckSquare size={16} className="text-blue-500" />
              ) : (
                <Square size={16} className="text-gray-400" />
              )}
            </button>
            <span className="font-medium">Select All</span>
          </div>

          <div className="relative">
            <input
              type="text"
              className="pl-8 pr-4 py-1 border rounded-lg"
              placeholder="Search inventory..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search
              className="absolute left-2 top-1.5 text-gray-400"
              size={16}
            />
          </div>
        </div>
        {isLoading ? <Spin size="large" className="p-8 flex justify-center items-center h-[40vh] w-full" /> : !compactView ? (
          <div className="divide-y">
            {inventories?.items.map((item) => (
              <div
                key={item.id}
                className="p-3 flex justify-between items-center hover:bg-gray-50"
              >
                <div className="flex items-center flex-1">
                  <button
                    className="mr-3"
                    onClick={() => toggleSelection(item.id)}
                  >
                    {selectedItems.includes(item.id) ? (
                      <CheckSquare size={16} className="text-blue-500" />
                    ) : (
                      <Square size={16} className="text-gray-400" />
                    )}
                  </button>

                  <div className="flex items-center space-x-2">
                    {item.itemType === InventoryType.PACKAGE ? (
                      <Box size={16} className="text-amber-500" />
                    ) : item.itemType === InventoryType.PART ? (
                      <Box size={16} className="text-blue-500" />
                    ) : (
                      <Package size={16} className="text-green-500" />
                    )}
                    <div>
                      <div className="font-medium">{item.title}</div>
                      <div className="text-sm text-gray-500">
                        {item.inventoryRef}
                        {item.clientRefNumber && ` • ${item.clientRefNumber}`}
                        {" • "}{formatPackingType(item.packingType, item.containerType)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-6">
                  <div className="text-sm text-gray-600">
                    {item.client?.companyName || [item.client?.firstName, item.client?.lastName]
                      .filter(Boolean)
                      .join(" ")
                      .trim() ||
                      "Unnamed Client"}
                  </div>
                  <div className="text-sm text-gray-600">
                    {item.location?.name}
                  </div>
                  {item.itemType !== InventoryType.PIECE &&
                    item.containedInventories && (
                      <div className="text-sm text-gray-500">
                        Contains {item.containedInventories.length} items
                      </div>
                    )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {inventories?.items.map((item) => (
              <div
                key={item.id}
                className={`flex items-center p-3 rounded-lg border ${selectedItems.includes(item.id)
                  ? "border-blue-500 bg-blue-50"
                  : "hover:bg-gray-50"
                  }`}
                onClick={() => toggleSelection(item.id)}
              >
                <div className="mr-2">
                  {selectedItems.includes(item.id) ? (
                    <CheckSquare size={16} className="text-blue-500" />
                  ) : (
                    <Square size={16} className="text-gray-400" />
                  )}
                </div>
                <div>
                  <div className="font-medium">{item.title}</div>
                  <div className="text-xs text-gray-500">
                    {item.inventoryRef}
                    {item.clientRefNumber && ` • ${item.clientRefNumber}`}
                    {" • "}{formatPackingType(item.packingType, item.containerType)}
                    {" • "}{item.location?.name}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {metaData.totalItems > 0 && (
          <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
            <Pagination
              total={metaData.totalItems}
              current={page}
              onChange={(page) => setPage(page)}
              itemRender={(_, type, originalElement) => {
                if (type === "prev") {
                  return <Button>Previous</Button>;
                }
                if (type === "next") {
                  return <Button>Next</Button>;
                }
                return originalElement;
              }}
            />
          </div>
        )}

        {inventories?.items.length === 0 && (
          <div className="flex flex-col justify-center items-center p-8 h-[33vh] text-center">
            <Package size={40} className="mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-700">
              No inventory items found
            </h3>
            <p className="text-gray-500 mt-1">
              Try adjusting your filter criteria
            </p>
          </div>
        )}
      </div>
    </div>
  );
};